import {useMemo, useState} from 'react';
import styled from 'styled-components';
import {LoadingBar} from '@/components/LoadingBar';
import {Tooltip} from '@/components/Tooltip';
import {Typography} from '@/components/Typography';
import {MEDALS} from '@/constants';
import {StoreCount} from '@/map/@types';
import {useMapDataSource} from '@/map/api';
import {Flexbox, FlexFullGrow, PaddingBox} from '@/styled-components';
import {palette} from '@/styles';
import {withComma} from '@/utils';
import {
  GrayBox,
  GrayCenterBox,
  SectionBlock,
  SectionSubTitle,
  SectionTab,
  SectionTabs,
  SectionTitle,
  SubValueText,
  ValueText,
} from '../../StyledComponents';
import {EMPTY_ITEM_TYPES, STORE_COUNT_TABS} from '../constants';
import {EmptyItem} from '../EmptyItem';
import {SalesDataProps} from '../interface';

export const StoreCountCompare = ({
  isPending,
  error,
  data: rawdata,
}: SalesDataProps) => {
  const {data: dataSource} = useMapDataSource();
  const metadata = useMemo(
    () =>
      dataSource?.find(
        (source) =>
          source.key === 'kodata' && source.data.indexOf('매출 정보') !== -1
      ),
    [dataSource]
  );

  const data = useMemo(() => {
    return rawdata?.map((i, idx) => ({
      ...(i?.storeCount || {}),
      idx,
    }));
  }, [rawdata]);

  return (
    <SectionBlock>
      <Flexbox>
        <SectionTitle>매장 수</SectionTitle>
        <Tooltip
          openOnClick
          closeEvents={{
            mouseout: false,
            blur: false,
            click: true,
          }}
          iconStyle={{marginLeft: 2}}
          content={`활성 매장 수란 총매장 수 중 카드 매출액이 존재하는 매장 (매출액 0원 초과)의 수 입니다.`}
        />
      </Flexbox>
      <SectionSubTitle>{`${metadata?.source || '카드사'}∙${
        metadata?.baseDate || ''
      }`}</SectionSubTitle>
      <PaddingBox top={16}>
        {isPending ? (
          <GrayCenterBox>
            <LoadingBar color={palette.textSecondary} />
          </GrayCenterBox>
        ) : error ? (
          <Typography variant="medium12" color={palette.textSecondary}>
            분석 실패
          </Typography>
        ) : !data ? (
          <EmptyItem type={EMPTY_ITEM_TYPES.Sales} />
        ) : (
          <StoreCountChartBox items={data} />
        )}
      </PaddingBox>
    </SectionBlock>
  );
};

const StoreCountChartBox = ({
  items,
}: {
  items: Array<StoreCount & {idx: number}>;
}) => {
  const [tab, setTab] = useState<number>(0);
  const tabKey = tab === 0 ? 'storeCount' : 'activeStoreCount';

  const ranking = useMemo(
    () => [...items].sort((a, b) => (b[tabKey] || 0) - (a[tabKey] || 0)),
    [items, tabKey]
  );

  return (
    <>
      <SectionTabs>
        {STORE_COUNT_TABS.map((i, idx) => (
          <SectionTab
            key={idx}
            active={idx === tab}
            onClick={() => setTab(idx)}
          >
            {i}
          </SectionTab>
        ))}
      </SectionTabs>
      <PaddingBox top={16}>
        <Typography variant="medium14">
          <Typography color={palette.primary500} fontWeight="700">
            {STORE_COUNT_TABS[tab]}
          </Typography>
          가 많은 상권은?
        </Typography>
        <PaddingBox top={10}>
          <GrayBox>
            <RankingBox>
              {ranking.map((i, ranking) => (
                <Rank key={`StoreCount-Rank-${i.idx}`}>
                  {MEDALS[ranking]}
                  <Typography variant="medium14" color={palette.textPrimary}>
                    비교 상권 {i.idx + 1}
                  </Typography>
                  <FlexFullGrow />
                  <div>
                    <ValueText>{withComma(i[tabKey] || 0)}</ValueText>{' '}
                    <SubValueText>개</SubValueText>
                  </div>
                </Rank>
              ))}
            </RankingBox>
          </GrayBox>
        </PaddingBox>
      </PaddingBox>
    </>
  );
};

const Rank = styled(Flexbox)`
  gap: 4px;
`;

const RankingBox = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
`;
